import { Navigate, Outlet, useRoutes } from "react-router-dom";
// layouts
import DashboardLayout from "./layouts/dashboard";

// Dashboard and Error Page
import NotFound from "./pages/Page404";

// Auth
import LoginPage from './pages/authPages/login/login';
import SignupPage from "./pages/authPages/signup/signup";

//===============================================================================
import http from "./services/http-common"
import RecoverPassword from "./pages/authPages/recoverPassword/recoverPassword"
import { Dashboard } from "./pages/main/dashboard/dashboard"
import { ProfilePage } from "./pages/main/profile"
import { ReviewPage } from "./pages/main/query/topic/review/ReviewPage";
import {useAuth} from "./contexts/auth2";
import {useEffect, useState} from "react";
import {getPlanType, getUserToken} from "./services/userService";
import {isCleoEnabled, isPatientManagementEnabled} from "./services/feature-flags.service";
import CreateNewPassword from "./pages/authPages/CreateNewPassword/CreateNewPassword";
import SupportPage from "./pages/main/support/SupportPage";
import UseTerms from "./pages/terms/UseTerms";
import PrivacyPolicy from "./pages/terms/PrivacyPolicy";
import Query from "./pages/main/query";
import {ManageActives} from "./pages/adm/actives/manage-actives";
import {ManageSubclass} from "./pages/adm/subclass/manage-subclass";
import { ProductPage } from "./pages/adm/Product/ProductPage";
import { VehiclePage } from "./pages/adm/Vehicle/VehiclePage";
import MercadoPagoPage from "./pages/notification/mercadoPago";
import {ManagePlans} from "./pages/adm/plans/manage-plans";
import { PlansPage } from "./pages/main/plans/plans-page";
import {HistoryPage} from "./pages/main/history/history-page";
import {ActivesPage} from "./pages/main/actives/actives-page";
import { GiftPage } from "./pages/adm/gift";
import { DosePage } from "./pages/main/query/topic/dose/dose-page";
import { UsersPage } from "./pages/adm/users/users-page";
import { DcoPage } from "./pages/adm/dco/dco-page";
import { Tutorial } from "./pages/adm/tutorial/tutorial";
import { TutorialPage } from "./pages/main/tutorial/TutorialPage";
import { ManageOral } from "./pages/adm/oral/manage-oral-actives";
import { DosageOral } from "./pages/main/query/oral/doseOral";
import { ReviewPageOral } from "./pages/main/query/oral/review";
import { HistoryPlan } from "./pages/main/historyPlan";
import { CouponPage } from "./pages/adm/coupon/coupon";
import { FeatureFlagPage } from "./pages/adm/feature-flag/feature-flag-page";
import { AIExperimentPage } from "./pages/adm/ai-experiment/ai-experiment-page";
import { CleoPage } from "./pages/main/cleo/CleoPage";
import { PatientsPage } from "./pages/main/patients/patients-page";
import { PatientDetailPage } from "./pages/main/patients/patient-detail-page";
import PaidRouteGuard from "./components/PaidRouteGuard";
import AuthRedirectPage from "./pages/authPages/authRedirect/auth-redirect";
import { AdminOverviewPage } from "./pages/adm/overview/admin-overview-page";

export default function Router() {

  const { hasUser, isAdm, user } = useAuth()
  const [ planType, setPlanType ] = useState<string>('free')
  const token = getUserToken()

  useEffect(() => {
    http.defaults.headers = {
      "Content-type": "application/json",
      Authorization: `Bearer ${token}`,
    }
  }, [ token ])

  useEffect(() => {
    setPlanType(getPlanType(user.plan))
  }, [ user ])

  return useRoutes([
    {
      path: "/dashboard",
      element:
        hasUser() ? <DashboardLayout /> : <Navigate to="/" />,
      children: [
        { index: true, element: <Navigate to="app" replace /> },
        { path: "app", element: <Dashboard /> },
        { path: 'profile', element: <ProfilePage /> },
        { path: 'pricing', element: <PlansPage /> },
        { path: 'query', element: <PaidRouteGuard isFree={planType === 'free'}><Query /></PaidRouteGuard> },
        { path: 'history', element: <PaidRouteGuard isFree={planType === 'free'}><HistoryPage /></PaidRouteGuard> },
        { path: 'actives', element: <PaidRouteGuard isFree={planType === 'free'}><ActivesPage /></PaidRouteGuard> },
        { path: 'dose', element: <PaidRouteGuard isFree={planType === 'free'}><DosePage /></PaidRouteGuard> },
        { path: 'doseOral', element: <PaidRouteGuard isFree={planType === 'free'}><DosageOral /></PaidRouteGuard> },
        { path: 'reviewConsult/:id', element: <PaidRouteGuard isFree={planType === 'free'}><ReviewPage /></PaidRouteGuard> },
        { path: 'reviewOral/:id', element: <PaidRouteGuard isFree={planType === 'free'}><ReviewPageOral /></PaidRouteGuard> },
        { path: 'tutorial', element: <TutorialPage/>},
        { path: 'planHistory', element: <HistoryPlan/>},
        {
          path: 'cleo',
          element: !isCleoEnabled()
            ? <Navigate to="/dashboard/app" replace />
            : <PaidRouteGuard isFree={planType === 'free'}><CleoPage /></PaidRouteGuard>,
        },
        {
          path: 'patients',
          element: !isPatientManagementEnabled()
            ? <Navigate to="/dashboard/app" replace />
            : <PaidRouteGuard isFree={planType === 'free'}><PatientsPage /></PaidRouteGuard>,
        },
        {
          path: 'patients/:id',
          element: !isPatientManagementEnabled()
            ? <Navigate to="/dashboard/app" replace />
            : <PaidRouteGuard isFree={planType === 'free'}><PatientDetailPage /></PaidRouteGuard>,
        },
        { path: 'support', element: <SupportPage /> }
      ],
    },
    {
      path: "/notification",
      children: [
        { path: "mercadopago", element: <MercadoPagoPage /> }
      ]
    },
    {
      path: "/",
      element: !hasUser() ? <Outlet /> : isAdm() ? <Navigate to="/dashboard/app" /> : <Navigate to="/dashboard/app" />,
      children: [
        { path: "/", element: <Navigate to="/login" /> },
        { path: "login", element: <LoginPage /> },
        { path: 'signup', element: <SignupPage/> },
        { path: "recoverPassword", element: <RecoverPassword/> },
        { path: 'newPassword/:token', element: <CreateNewPassword /> },
        { path: "404", element: <NotFound /> },
        { path: "*", element: <Navigate to="/404" /> },
      ],
    },
    {
      path: '/adm',
      element:
        hasUser() ? 
        isAdm() ? <DashboardLayout /> : <Navigate to="/" /> :
        <Navigate to="/" />,
      children: [
        { path: '', element: <AdminOverviewPage /> },
        { path: 'users', element: <UsersPage /> },
        { path: 'actives', element: <ManageActives /> },
        { path: 'oral', element: <ManageOral/>},
        { path: 'subclass', element: <ManageSubclass /> },
        { path: 'products', element: <ProductPage />},
        { path: 'vehicles', element: <VehiclePage />},
        { path: 'plans', element: <ManagePlans />},
        { path: 'gifts', element: <GiftPage />},
        { path: 'DCO', element: <DcoPage />},
        { path: 'tutorial', element: <Tutorial/>},
        { path: 'cupom', element: <CouponPage/> },
        { path: 'feature-flags', element: <FeatureFlagPage/> },
        { path: 'ai-experiments', element: <AIExperimentPage/> },
      ]
    },
    { path: 'auth-redirect', element: <AuthRedirectPage /> },
    { path: 'use-terms', element: <UseTerms /> },
    { path: 'privacy-policy', element: <PrivacyPolicy /> },
    { path: "*", element: <Navigate to="/404" replace /> },
  ]);
}

